import { useEffect, useRef, useState } from "react";
import { BTN_SOLID_DANGER, CHIP, CHIP_TALL } from "../homeTokens";

/**
 * Central de avisos — the persistent home for platform broadcasts.
 *
 * Until now a broadcast appeared once as a modal and was gone; if you
 * dismissed it you had no way back to it. This drawer is the missing archive:
 * everything the platform sent stays readable after it has been acknowledged.
 *
 * A right-side drawer rather than a page because reading a notice is almost
 * never the reason someone opened the app — the user should be able to check
 * and return without losing their place.
 */

const TAG_TONE = {
  critico:
    "bg-[rgba(224,72,58,.12)] text-[#c23b2f] dark:bg-[rgba(255,90,77,.16)] dark:text-[#ff8b7f]",
  financeiro:
    "bg-[rgba(217,134,19,.12)] text-[#b06d0c] dark:bg-[rgba(255,168,40,.16)] dark:text-[#ffbe63]",
  manutencao: "bg-black/[.06] text-[#5c5f70] dark:bg-white/[.09] dark:text-white/70",
  produto:
    "bg-[rgba(91,91,214,.12)] text-[#4f4fc4] dark:bg-[rgba(139,139,255,.16)] dark:text-[#b0b0ff]",
};

const FILTERS = [
  { id: "todos", label: "Todos" },
  { id: "nao-lidos", label: "Não lidos" },
  { id: "criticos", label: "Críticos" },
  { id: "financeiro", label: "Financeiro" },
];

export default function NoticeDrawer({ open, notices = [], onClose, onOpenNotice }) {
  const [filter, setFilter] = useState("todos");
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    panelRef.current?.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const counts = {
    todos: notices.length,
    "nao-lidos": notices.filter((n) => !n.read).length,
    criticos: notices.filter((n) => n.tone === "critico").length,
    financeiro: notices.filter((n) => n.tone === "financeiro").length,
  };

  const visible = notices.filter((notice) => {
    if (filter === "nao-lidos") return !notice.read;
    if (filter === "criticos") return notice.tone === "critico";
    if (filter === "financeiro") return notice.tone === "financeiro";
    return true;
  });

  return (
    <div className="fixed inset-0 z-[80]">
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar clicando fora"
        tabIndex={-1}
        className="absolute inset-0 cursor-default border-0 bg-black/40 backdrop-blur-[3px] dark:bg-black/60"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Central de avisos"
        tabIndex={-1}
        className="absolute bottom-0 right-0 top-0 flex w-[min(470px,94vw)] flex-col border-l border-black/[.08] bg-[#f7f8fc] font-jakarta outline-none dark:border-white/10 dark:bg-[#0c0c12] animate-slide-in-right motion-reduce:animate-none"
      >
        <div className="border-b border-black/[.07] px-[26px] pb-[18px] pt-6 dark:border-white/[.08]">
          <div className="flex items-center gap-3">
            <h2 className="text-[19px] font-bold leading-[1.2] tracking-[-.02em] text-[#15161f] dark:text-white">
              Central de avisos
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar central de avisos"
              className="ml-auto h-7 w-7 rounded-lg border border-black/[.12] bg-transparent text-[13px] font-medium text-[#5c5f70] transition-colors hover:bg-black/5 hover:text-[#15161f] dark:border-white/[.14] dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white"
            >
              ✕
            </button>
          </div>
          <p className="mt-[5px] text-[12.5px] leading-[1.55] text-[#5c5f70] dark:text-white/45">
            Comunicados enviados pela plataforma para a sua conta. Ficam aqui mesmo
            depois de lidos.
          </p>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {FILTERS.map((option) => {
              const isActive = filter === option.id;
              const count = counts[option.id];
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setFilter(option.id)}
                  aria-pressed={isActive}
                  className={
                    isActive
                      ? "inline-flex flex-none items-center whitespace-nowrap rounded-2xl bg-[#15161f] px-3 py-1.5 text-[11.5px] font-bold text-white dark:bg-white dark:text-[#0a0a0f]"
                      : CHIP
                  }
                >
                  {option.label}
                  {count ? ` · ${count}` : ""}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-auto px-[26px] pb-[30px] pt-1.5">
          {visible.length === 0 ? (
            <p className="pt-10 text-center text-[13px] text-[#8a8d9e] dark:text-white/40">
              Nenhum aviso neste filtro.
            </p>
          ) : (
            visible.map((notice) => (
              <article
                key={notice.id}
                className="border-b border-black/[.07] py-[22px] dark:border-white/[.07]"
              >
                <div className="mb-2.5 flex items-center gap-2.5">
                  <span
                    className={`rounded-md px-[9px] py-[3px] text-[9.5px] font-bold uppercase leading-[1.4] tracking-[.08em] ${
                      TAG_TONE[notice.tone] || TAG_TONE.produto
                    }`}
                  >
                    {notice.tagLabel}
                  </span>
                  <span className="font-mono text-[11px] leading-none text-[#9a9dad] dark:text-white/[.32]">
                    {notice.when}
                  </span>
                  {!notice.read && (
                    <span
                      aria-label="Não lido"
                      className="ml-auto h-1.5 w-1.5 rounded-full bg-[#4f7dff] dark:bg-[#7b9bff]"
                    />
                  )}
                </div>
                <h3
                  className={`text-[15px] font-bold leading-[1.35] tracking-[-.015em] ${
                    notice.read
                      ? "text-[#5c5f70] dark:text-white/60"
                      : "text-[#15161f] dark:text-white"
                  }`}
                >
                  {notice.title}
                </h3>
                <p className="mt-1.5 text-[12.5px] leading-[1.6] text-[#5c5f70] dark:text-white/45">
                  {notice.summary}
                </p>
                <div className="mt-3.5 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => onOpenNotice?.(notice)}
                    className={notice.tone === "critico" ? BTN_SOLID_DANGER : CHIP_TALL}
                  >
                    {notice.cta}
                  </button>
                  <span className="text-[11.5px] leading-none text-[#9a9dad] dark:text-white/30">
                    {notice.read ? "Leitura confirmada" : "Confirma leitura ao abrir"}
                  </span>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
